import { useEffect, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
    CurrencyDollar, Package, Clock, CheckCircle, EnvelopeSimple, Receipt, BellRinging, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { api, authHeaders } from "@/lib/api";
import StatCard from "@/admin/components/StatCard";
import PageHeader from "@/admin/components/PageHeader";

export default function Overview() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        const fetchAll = () =>
            api.get("/admin/stats", { headers: authHeaders() })
                .then((r) => setStats(r.data))
                .catch(() => {});
        fetchAll();
        const id = setInterval(fetchAll, 12000);
        return () => clearInterval(id);
    }, []);

    return (
        <div data-testid="admin-overview">
            <PageHeader kicker="Dashboard" title="Operations" />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard icon={CurrencyDollar} label="Revenue"
                    value={stats ? `$${stats.revenue.toFixed(2)}` : "$0.00"} testid="stat-revenue" />
                <StatCard icon={Receipt} label="Paid orders"
                    value={stats?.orders_total ?? 0} testid="stat-orders" />
                <StatCard icon={Clock} label="Pending"
                    value={stats?.pending ?? 0} testid="stat-pending" />
                <StatCard icon={Package} label="Preparing"
                    value={stats?.preparing ?? 0} testid="stat-preparing" />
                <StatCard icon={CheckCircle} label="Delivered"
                    value={stats?.delivered ?? 0} testid="stat-delivered" />
                <StatCard icon={EnvelopeSimple} label="Subscribers"
                    value={stats?.subscribers ?? 0} testid="stat-subscribers" />
            </div>

            <Card className="p-6 mt-6">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Recent orders
                </p>
                <h2 className="font-display font-black text-2xl mt-1 mb-4">Revenue snapshot</h2>
                <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats?.daily ?? []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip cursor={{ fill: "rgba(232,120,17,0.08)" }} />
                            <Bar dataKey="total" fill="#E87811" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card className="p-6 mt-6 bg-gradient-to-br from-primary/5 to-secondary/10 border-primary/20">
                <p className="text-xs font-bold uppercase tracking-widest text-primary">
                    Staff training
                </p>
                <h2 className="font-display font-black text-2xl mt-1">Probar alerta de orden</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Genera una orden de demostración para que el equipo vea exactamente
                    cómo entra y suena un pedido real. Pickup suena con tono alegre y
                    Delivery con tono más urgente. Las órdenes se marcan con
                    “(TEST)” y puedes borrarlas con un click.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                    <button
                        data-testid="trigger-test-alert"
                        onClick={async () => {
                            try {
                                await api.post(
                                    "/admin/orders/test-trigger?fulfillment=pickup",
                                    {},
                                    { headers: authHeaders() },
                                );
                            } catch {
                                /* noop */
                            }
                        }}
                        className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-3 font-bold uppercase tracking-widest text-xs transition hover:-translate-y-0.5"
                    >
                        <BellRinging size={14} weight="fill" />
                        Probar Pickup
                    </button>
                    <button
                        data-testid="trigger-test-alert-delivery"
                        onClick={async () => {
                            try {
                                await api.post(
                                    "/admin/orders/test-trigger?fulfillment=delivery",
                                    {},
                                    { headers: authHeaders() },
                                );
                            } catch {
                                /* noop */
                            }
                        }}
                        className="inline-flex items-center gap-2 rounded-full bg-foreground hover:bg-foreground/90 text-background px-5 py-3 font-bold uppercase tracking-widest text-xs transition hover:-translate-y-0.5"
                    >
                        <BellRinging size={14} weight="fill" />
                        Probar Delivery
                    </button>
                    <button
                        data-testid="cleanup-test-orders"
                        onClick={async () => {
                            if (!window.confirm("Borrar todas las órdenes de prueba (TEST)?")) return;
                            try {
                                const r = await api.delete(
                                    "/admin/orders/test-cleanup",
                                    { headers: authHeaders() },
                                );
                                toast.success(
                                    `Borradas ${r.data.deleted} órdenes de prueba`,
                                );
                            } catch (e) {
                                toast.error(
                                    e?.response?.data?.detail || "No se pudo limpiar",
                                );
                            }
                        }}
                        className="inline-flex items-center gap-2 rounded-full border-2 border-border hover:border-foreground text-foreground px-5 py-3 font-bold uppercase tracking-widest text-xs transition"
                    >
                        <Trash size={14} weight="bold" />
                        Limpiar órdenes TEST
                    </button>
                </div>
            </Card>

        </div>
    );
}
